import { ReviewingParty } from "@redotech/cockroachdb-codegen/congodb/reviewing-party";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { Big } from "big.js";
import { DraftReturn } from "./draft-return";
import { getPrimaryProduct, getReturnableItemTotalReturnValue } from "./util";

const CLAIM_DOCUMENTS_VALUE_THRESHOLD_USD = Big(1000);

/**
 * Answer codes whose claims require a police report: "stolen" = loss after
 * delivery (Shipsurance's "Loss after Delivery" column).
 */
export const POLICE_REPORT_REASON_CODES = ["stolen"] as const;

export function shouldCollectPoliceReport(draftReturn: DraftReturn): boolean {
  return draftReturn.pendingReturnItems.some((pendingItem) =>
    pendingItem.reasonCodes.some((code) =>
      POLICE_REPORT_REASON_CODES.includes(
        code as (typeof POLICE_REPORT_REASON_CODES)[number],
      ),
    ),
  );
}

/**
 * Redo-reviewed Package Protection Plus claims worth at least $1,000 collect a
 * government ID and supporting documentation once per return.
 */
export function shouldCollectReturnLevelClaimDocuments({
  draftReturn,
  packageProtectionPlusEnabled,
  ppIdCollectionEnabled,
}: {
  draftReturn: DraftReturn;
  packageProtectionPlusEnabled: boolean;
  ppIdCollectionEnabled: boolean;
}): boolean {
  if (!ppIdCollectionEnabled) {
    return false;
  }
  if (!packageProtectionPlusEnabled) {
    return false;
  }
  if (draftReturn.returnType !== ReturnTypeEnum.CLAIM) {
    return false;
  }
  if (draftReturn.reviewingParty !== ReviewingParty.Redo) {
    return false;
  }
  if (!draftReturn.orders.some((order) => order.packageProtected)) {
    return false;
  }

  const selectedItemsValuePresentment = draftReturn.returnableItems
    .filter((item) => item.selected)
    .reduce(
      (total, item) =>
        total.plus(
          getReturnableItemTotalReturnValue(
            item,
            getPrimaryProduct(item).quantity,
          ),
        ),
      Big(0),
    );

  const usdToPresentmentRate = Big(
    draftReturn.currentUsdToPresentmentExchangeRate ?? 1,
  );
  const selectedItemsValueUsd =
    selectedItemsValuePresentment.div(usdToPresentmentRate);

  return selectedItemsValueUsd.gte(CLAIM_DOCUMENTS_VALUE_THRESHOLD_USD);
}
